import { Table, Spinner } from "react-bootstrap";
import { config } from "../config/config";
import { getForYouCategory } from "../config/url";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState, useEffect, useRef } from "react";
import useFetchData from "../hooks/useFetchData";
import axios from "axios";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { toast } from "react-toastify";

// Sortable table row component
const SortableTableRow = ({ category, index, onEdit }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: "white",
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
    position: "relative"
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes}>
      <td>
        <div className="d-flex align-items-center">
          <span
            {...listeners}
            className="drag-handle me-5"
            style={{ cursor: "grab", color: "#888" }}
          >
            ☰
          </span>
          {(index + 1).toString().padStart(4, "0")}
        </div>
      </td>
      <td>{category.name}</td>
      <td>{category.categoryType?.charAt(0).toUpperCase() + category.categoryType?.slice(1) || "Free"}</td>
      <td>
        <button
          className="app-view-btn"
          onClick={() => onEdit(category)}
        >
          View
        </button>
      </td>
    </tr>
  );
};

const ForYouCategory = () => {
  const user = useSelector((state) => state?.user);
  const [allCategories, setAllCategories] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const lastProcessedDataRef = useRef(null);

  const businessId = user.user?.businessId;
  const businessIdentifier = user.user?.businessIdentifier;

  const navigate = useNavigate();

  const categories = useFetchData(
    `${getForYouCategory}?businessId=${businessId}&businessIdentifier=${businessIdentifier}`,
    config
  );

  useEffect(() => {
    // Only update categories when loading is complete
    if (categories?.loading === false && categories?.data !== undefined) {
      // Create a stable identifier for the data to avoid reprocessing the same data
      const dataIdentifier = categories.data 
        ? JSON.stringify(categories.data.map(cat => ({ id: cat.id, position: cat.position })))
        : null;
      
      // Only process if data has actually changed
      if (dataIdentifier !== lastProcessedDataRef.current) {
        lastProcessedDataRef.current = dataIdentifier;
        
        if (Array.isArray(categories.data) && categories.data.length > 0) {
          // Make a copy and ensure each item has a position property
          const sortedCategories = [...categories.data].map((cat, index) => ({
            ...cat,
            position: cat.position || index + 1
          }));
          
          // Sort by position
          sortedCategories.sort((a, b) => a.position - b.position);
          
          setAllCategories(sortedCategories);
        } else {
          // Explicitly set empty array when response comes back with no categories or null/undefined
          setAllCategories([]);
        }
      }
    }
    // Only depend on loading state and data length to avoid infinite loops
    // The ref check inside ensures we don't reprocess the same data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories?.loading, categories?.data?.length]);

  const handleEditCategory = (category) => {
    navigate(`/for-you-category/${category.id}`, {
      state: { category },
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event) => {
    // Drag started - handlers can use event.active.id if needed
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setAllCategories((categories) => {
        // Find the indices in the full dataset
        const oldIndex = categories.findIndex((cat) => cat.id === active.id);
        const newIndex = categories.findIndex((cat) => cat.id === over.id);
        
        // Create the reordered array
        const reordered = arrayMove(categories, oldIndex, newIndex);
        
        // Update positions for all items
        const updatedCategories = reordered.map((cat, index) => ({
          ...cat,
          position: index + 1
        }));
        
        setHasChanges(true);
        return updatedCategories;
      });
    }
  };

  const savePositions = async () => {
    try {
      // Create positions array with index-based positions to ensure they match the current order
      const positions = allCategories.map((category, index) => ({
        id: category.id,
        position: index + 1, // Explicitly set position based on current array order
      }));
  
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/for-you-category/positions`,
        {
          positions,
          businessId,
          businessIdentifier,
        },
        config
      );
  
      if (response.status === 200) {
        setHasChanges(false);
        // Show success message
        // alert("Category order saved successfully!");
        toast.success("Category order saved successfully!");
        
        // Update the allCategories state to reflect the saved positions
        setAllCategories(prev => 
          prev.map((cat, index) => ({
            ...cat,
            position: index + 1
          }))
        );
      }
    } catch (error) {
      console.error("Error saving positions:", error);
      toast.error("Failed to save category order. Please try again.");
    }
  }; 

  return (
    <div className="app-page">
      <div className="bg-white rounded shadow-sm">
        <div className="app-page-toolbar">
          <h2 className="app-page-title">For You Category</h2>
          <div className="app-page-toolbar-filters">
            {hasChanges && (
              <button
                className="primaryDarkBtn app-filter-action-btn"
                style={{ backgroundColor: "#198754" }}
                onClick={savePositions}
              >
                Save Order
              </button>
            )}
            <button
              className="app-filter-action-btn"
              onClick={() => navigate("/for-you-category/add")}
            >
              Add New
            </button>
          </div>
        </div>
        
        <div>
          {categories?.loading === true ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <div className="mt-3 text-muted">Loading for you categories...</div>
            </div>
          ) : allCategories.length === 0 ? (
            <div className="text-center p-4">No categories found.</div>
          ) : (
            <>
              {hasChanges && (
                <div className="alert alert-info mb-3 mx-3" role="alert">
                  <small>Drag and drop categories to reorder them. Click "Save Order" when finished.</small>
                </div>
              )}
              
              <Table
                className="mb-0"
                hover
                responsive
                style={{ borderCollapse: "collapse", borderSpacing: 0 }}
              >
                <thead
                  style={{
                    backgroundColor: "#064260",
                    color: "white",
                    height: "50px",
                    textAlign: "center",
                  }}
                >
                  <tr>
                    <th className="text-start" style={{paddingLeft: "80px"}}>
                      <span>
                      ID
                      </span>
                    </th>
                    <th>Name</th>
                    <th>Category Type</th>
                    <th style={{minWidth: "160px", maxWidth: "160px", width: "160px"}}>Action</th>
                  </tr>
                </thead>
                
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  modifiers={[restrictToVerticalAxis]}
                >
                  <tbody
                    style={{
                      color: "#575757",
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      fontWeight: 500,
                      textAlign: "center",
                    }}
                  >
                    <SortableContext
                      className="justify-content-center"
                      items={allCategories?.map(category => category.id) || []}
                      strategy={verticalListSortingStrategy}
                    >
                      {allCategories &&
                        allCategories.map((category, index) => (
                          <SortableTableRow
                            key={category.id}
                            category={category}
                            index={index}
                            onEdit={handleEditCategory}
                          />
                        ))}
                    </SortableContext>
                  </tbody>
                </DndContext>
              </Table>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForYouCategory;